import type { Account } from '~/shared/utils/types';

import { buildAccountCache } from './buildAccountCache';
import type { ExplorationAccountCache, ExplorationVolume } from '../types/exploration';

export interface OrgParentEntry {
  accountId: string;
  name: string;
  isVirtual: boolean;
  childAccountIds: string[];
}

export interface OrgAccountGroup {
  accountId: string;
  name: string;
  vols: ExplorationVolume[];
  count: number;
  totalSize: number;
}

type AccountWithParentName = Account & { parentAccountName?: string };

function normalizeAccountName(name: string): string {
  return name.trim().toLowerCase();
}

function buildIdByName(accounts: Account[]): Record<string, string> {
  const idByName: Record<string, string> = {};
  accounts.forEach((a) => {
    idByName[a.accountId] = a.accountId;
    if (!a.accountName) return;
    idByName[a.accountName] = a.accountId;
    idByName[normalizeAccountName(a.accountName)] = a.accountId;
  });
  return idByName;
}

function lookupAccountIdByName(name: string, idByName: Record<string, string>): string | null {
  return idByName[name] || idByName[normalizeAccountName(name)] || null;
}

function rawParentIdOf(a: AccountWithParentName, idByName: Record<string, string>): string | null {
  if (a.parentAccountId && a.parentAccountId !== a.accountId) {
    return a.parentAccountId;
  }
  if (a.parentAccountName) {
    return lookupAccountIdByName(a.parentAccountName, idByName);
  }
  return null;
}

/** Union resolved children with any auth accounts linked by parentAccountId. */
export function enrichOrgParentEntry(entry: OrgParentEntry, accounts: Account[]): OrgParentEntry {
  const childIds = new Set(entry.childAccountIds);
  accounts.forEach((a) => {
    if (a.parentAccountId && a.parentAccountId !== a.accountId && a.parentAccountId === entry.accountId) {
      childIds.add(a.accountId);
    }
  });
  return { ...entry, childAccountIds: [...childIds] };
}

function isRootAccount(a: AccountWithParentName, idByName: Record<string, string>): boolean {
  const pid = rawParentIdOf(a, idByName);
  return !pid || pid === a.accountId;
}

/** Resolves org parent/child structure from auth accounts. */
export function resolveOrgParentEntries(accounts: Account[]): OrgParentEntry[] {
  if (!accounts.length) return [];

  const idByName = buildIdByName(accounts);
  const accessibleIds = new Set(accounts.map((a) => a.accountId));

  const childrenByParentId = new Map<string, Account[]>();
  accounts.forEach((a) => {
    const pid = rawParentIdOf(a, idByName);
    if (!pid) return;
    if (!childrenByParentId.has(pid)) childrenByParentId.set(pid, []);
    childrenByParentId.get(pid)!.push(a);
  });

  const parentEntries: OrgParentEntry[] = [];
  const parentEntryIds = new Set<string>();
  const childIdsClaimed = new Set<string>();

  accounts.forEach((p) => {
    if (!isRootAccount(p, idByName)) return;
    const children = childrenByParentId.get(p.accountId) || [];
    parentEntries.push({
      accountId: p.accountId,
      name: p.accountName,
      isVirtual: false,
      childAccountIds: children.map((c) => c.accountId),
    });
    parentEntryIds.add(p.accountId);
    children.forEach((c) => childIdsClaimed.add(c.accountId));
  });

  childrenByParentId.forEach((children, pid) => {
    if (accessibleIds.has(pid)) return;
    if (children.length < 2) return;
    const name = (children[0] as AccountWithParentName).parentAccountName || 'Org';
    parentEntries.push({
      accountId: pid,
      name,
      isVirtual: true,
      childAccountIds: children.map((c) => c.accountId),
    });
    parentEntryIds.add(pid);
    children.forEach((c) => childIdsClaimed.add(c.accountId));
  });

  accounts.forEach((a) => {
    if (childIdsClaimed.has(a.accountId)) return;
    if (parentEntryIds.has(a.accountId)) return;
    parentEntries.push({
      accountId: a.accountId,
      name: a.accountName,
      isVirtual: false,
      childAccountIds: [],
    });
    parentEntryIds.add(a.accountId);
  });

  return parentEntries;
}

export function getOrgMemberAccountIds(entry: OrgParentEntry): string[] {
  if (entry.isVirtual) return [...entry.childAccountIds];
  return [entry.accountId, ...entry.childAccountIds];
}

export function orgKeyForParent(parentId: string): string {
  return `org:${parentId}`;
}

export function parentIdFromOrgKey(orgKey: string): string | null {
  if (!orgKey.startsWith('org:')) return null;
  return orgKey.slice(4) || null;
}

/** Ensures empty cache shells exist for org member accounts (dropdown completeness). */
export function ensureOrgMemberCaches(
  caches: Record<string, ExplorationAccountCache>,
  entry: OrgParentEntry,
  nameMap: Record<string, string>
): void {
  getOrgMemberAccountIds(entry).forEach((acctId) => {
    if (!caches[acctId]) {
      caches[acctId] = buildAccountCache(acctId, nameMap[acctId] || acctId, []);
    }
  });
}

/** Per-account cache volumes (pre-org-dedupe). Views use deduped org volumes instead. */
export function buildOrgAccountGroups(
  caches: Record<string, ExplorationAccountCache>,
  orgKey: string,
  filterFn?: (vols: ExplorationVolume[]) => ExplorationVolume[]
): OrgAccountGroup[] {
  const orgCache = caches[orgKey];
  const childIds = orgCache?._orgChildIds ?? [];
  const parentId = parentIdFromOrgKey(orgKey);
  const isVirtual = orgCache?._isVirtualOrg ?? false;

  const memberIds = isVirtual ? childIds : parentId ? [parentId, ...childIds] : childIds;

  const groups: OrgAccountGroup[] = memberIds.map((acctId) => {
    const cache = caches[acctId];
    const rawVols = cache?.volumes ?? [];
    const vols = filterFn ? filterFn(rawVols) : rawVols;
    return {
      accountId: acctId,
      name: cache?.name || acctId,
      vols,
      count: vols.length,
      totalSize: vols.reduce((s, v) => s + (v.size || 0), 0),
    };
  });

  return groups.filter((g) => g.count > 0).sort((a, b) => b.totalSize - a.totalSize);
}

export function shouldBuildOrgAggregate(entry: OrgParentEntry): boolean {
  if (entry.isVirtual) return entry.childAccountIds.length >= 2;
  return entry.childAccountIds.length > 0;
}
